import {indexBy, isNil} from 'ramda';

import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {GetAvailableTemplatesRequest, useGetAvailableTemplatesQuery} from '@dms/api/warehouse';
import {useTenant} from '@dms/shared';

import {getGroupedTemplateOptions} from '../utils/getGroupedTemplateOptions';

/**
 * Provides entity-scoped, authorization-profile-grouped template options for the warehouse
 * settings document dropdowns. Pass exactly one of warehouseId / directSaleVariantId /
 * supplierOrderVariantId (or none while creating a new entity).
 */
export function useWarehouseAvailableTemplates(queryArg: GetAvailableTemplatesRequest) {
  const {tenant} = useTenant();

  const {
    data: availableDocuments,
    isLoading: isAvailableDocumentsLoading,
    isError: isAvailableDocumentsError,
  } = useGetAvailableTemplatesQuery(queryArg);

  const {
    data: authorizationProfiles,
    isLoading: isAuthorizationProfilesLoading,
    isError: isAuthorizationProfilesError,
  } = useGetAuthorizationProfilesQuery({'x-tenant': tenant?.id ?? ''}, {skip: isNil(tenant)});

  const authorizationProfilesById = indexBy((profile) => profile.id, authorizationProfiles ?? []);

  const getOptions = (documentCode: string) =>
    getGroupedTemplateOptions(availableDocuments, authorizationProfilesById, documentCode);

  return {
    getOptions,
    isLoading: isAvailableDocumentsLoading || isAuthorizationProfilesLoading,
    isError: isAvailableDocumentsError || isAuthorizationProfilesError,
  };
}
